import {Scenario, ScenarioType} from '@types';

export type ScenarioTexts = {
    ownershipGood: {
        positive?: string;
        negativeBad: string;
        negativeLittle: string;
    };
    ownershipBad: string;
    successSubtitle: string;
    problemLabelKey: string;
    researchPageTitleKey: string;
    researchHeadingKey: string;
};

export type TrackingInfo = {
    trackPlayerStones: boolean;
    hasTarget: boolean;
};

export const scenarioTextsMap: Record<ScenarioType, ScenarioTexts> = {
    [ScenarioType.INVADE]: {
        ownershipGood: {
            positive: 'invasions.status_alive',
            negativeBad: 'invasions.status_alive_lost_points_bad',
            negativeLittle: 'invasions.status_alive_lost_points_little',
        },
        ownershipBad: 'invasions.status_stones_danger',
        successSubtitle: 'invasions.status_invaded_successfully',
        problemLabelKey: 'invasions.problem',
        researchPageTitleKey: 'invasions.research_page_title',
        researchHeadingKey: 'invasions.research_heading',
    },
    [ScenarioType.REPEL]: {
        ownershipGood: {
            positive: 'invasions.status_repel_dying',
            negativeBad: 'invasions.status_repel_dying_lost_points_bad',
            negativeLittle: 'invasions.status_repel_dying_lost_points_little',
        },
        ownershipBad: 'invasions.status_repel_alive',
        successSubtitle: 'invasions.status_repelled_successfully',
        problemLabelKey: 'invasions.invader_repel',
        researchPageTitleKey: 'invasions.research_repel_page_title',
        researchHeadingKey: 'invasions.research_repel_heading',
    },
    [ScenarioType.SCENARIO]: {
        ownershipGood: {
            positive: 'invasions.status_scenario_looking_good',
            negativeBad:
                'invasions.status_scenario_looking_good_lost_points_bad',
            negativeLittle:
                'invasions.status_scenario_looking_good_lost_points_little',
        },
        ownershipBad: 'invasions.status_scenario_not_looking_good',
        successSubtitle: 'invasions.status_scenario_completed_successfully',
        problemLabelKey: 'invasions.scenario',
        researchPageTitleKey: 'invasions.research_scenario_page_title',
        researchHeadingKey: 'invasions.research_scenario_heading',
    },
};

export const getScenarioTexts = (type: ScenarioType): ScenarioTexts => {
    return scenarioTextsMap[type] ?? scenarioTextsMap[ScenarioType.INVADE];
};

/**
 * Derive ownership tracking info for a scenario.
 *
 * For INVADE: track player's own stones (must survive).
 * For REPEL:  track computer's stones (must die).
 * For SCENARIO: derived from metadata.targetGoal:
 *   - 'live' → track player's own stones (must survive)
 *   - 'kill' → track opponent's stones (must die)
 *   - missing → hasTarget=false (no ownership tracking)
 */
export const deriveTrackingInfo = (scenario: Scenario): TrackingInfo => {
    if (scenario.type === ScenarioType.INVADE) {
        return {trackPlayerStones: true, hasTarget: true};
    }
    if (scenario.type === ScenarioType.REPEL) {
        return {trackPlayerStones: false, hasTarget: true};
    }

    const goal = scenario.metadata?.targetGoal;
    if (!goal) {
        return {trackPlayerStones: false, hasTarget: false};
    }

    return {trackPlayerStones: goal === 'live', hasTarget: true};
};
